'use client';

import { type ReactNode, useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import { PublicLink } from '@/components/PublicLink';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PreviewDataTable } from '@/components/preview/PreviewDataTable';
import type { DatasetSummary } from '@/types/templates';
import { isUserDatasetSummary } from '@/lib/datasetPublicAccess';
import { formatUtcDateTime } from '@/lib/time';
import {
  EmptyState,
  PageHero,
  PageIntro,
  PageSection,
  SectionPill,
  SurfacePanel,
} from '@/components/site/page-primitives';

import { DatasetCatalogSwitch, type DatasetCatalogView } from './DatasetCatalogSwitch';

const CATALOG_CARD_PREVIEW_ROWS = 4;

function formatDate(value: string): string {
  return formatUtcDateTime(value, value);
}

function getCatalogCardPreviewRows(dataset: DatasetSummary) {
  return (dataset.sampleRows ?? []).slice(0, CATALOG_CARD_PREVIEW_ROWS);
}

function DatasetCard({
  dataset,
  actionLabel,
  showPreview,
  timestampLabel,
  timestampValue,
  primaryAction = false,
}: {
  dataset: DatasetSummary;
  actionLabel: string;
  showPreview: boolean;
  timestampLabel: string;
  timestampValue: string;
  primaryAction?: boolean;
}) {
  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="space-y-3">
        <CardTitle className="text-lg">{dataset.name}</CardTitle>
        <CardDescription>{dataset.description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="text-xs uppercase tracking-[0.16em] text-[var(--site-ink-subtle)]">
          {timestampLabel}: {formatDate(timestampValue)}
        </div>
        {showPreview ? (
          <PreviewDataTable
            tableId={`${dataset.id}-card-preview`}
            rows={getCatalogCardPreviewRows(dataset)}
            emptyMessage="Строки превью недоступны."
            variant="compact"
            enableColumnControls={false}
            pinFirstColumn={false}
          />
        ) : null}
        <div className="mt-auto">
          <PublicLink href={`/datasets/${dataset.id}`} className="block">
            <Button variant={primaryAction ? 'default' : 'outline'} className="w-full">
              {actionLabel}
            </Button>
          </PublicLink>
        </div>
      </CardContent>
    </Card>
  );
}

function DatasetSection({
  title,
  datasets,
  emptyTitle,
  emptyDescription,
  emptyAction,
  actionLabel,
  showPreview,
  timestampLabel,
  getTimestampValue,
  primaryAction,
}: {
  title: string;
  datasets: DatasetSummary[];
  emptyTitle: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  actionLabel: string;
  showPreview: boolean;
  timestampLabel: string;
  getTimestampValue: (dataset: DatasetSummary) => string;
  primaryAction?: boolean;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold text-[var(--site-ink-strong)]">{title}</h2>
        <SectionPill>{`Найдено: ${datasets.length}`}</SectionPill>
      </div>
      {datasets.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {datasets.map((dataset) => (
            <DatasetCard
              key={dataset.id}
              dataset={dataset}
              actionLabel={actionLabel}
              showPreview={showPreview}
              timestampLabel={timestampLabel}
              timestampValue={getTimestampValue(dataset)}
              primaryAction={primaryAction}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function DatasetsCatalogClient({
  initialDatasets,
  initialError,
  activeView,
}: {
  initialDatasets: DatasetSummary[];
  initialError: string | null;
  activeView: DatasetCatalogView;
}) {
  const [search, setSearch] = useState('');
  const hasSearch = search.trim().length > 0;

  const filteredDatasets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return initialDatasets;
    }

    return initialDatasets.filter((dataset) => {
      const haystack = `${dataset.name} ${dataset.description}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [initialDatasets, search]);

  const publicUtilityDatasets =
    activeView === 'public'
      ? filteredDatasets.filter((dataset) => dataset.visibility === 'public_utility')
      : [];
  const publicPreviewDatasets =
    activeView === 'public'
      ? filteredDatasets.filter((dataset) => dataset.visibility === 'public_preview')
      : [];
  const userDatasets = activeView === 'user' ? filteredDatasets.filter(isUserDatasetSummary) : [];

  const pageTitle = activeView === 'user' ? 'Ваши датасеты' : 'Открытые датасеты';
  const pageDescription =
    activeView === 'user'
      ? 'Результаты ваших запусков и командные наборы данных.'
      : 'Публичные примеры и опубликованные наборы данных.';
  const searchTitle = activeView === 'user' ? 'Поиск ваших датасетов' : 'Поиск открытых датасетов';
  const searchDescription =
    activeView === 'user'
      ? 'Фильтруйте приватные и командные результаты запусков.'
      : 'Ищите опубликованные датасеты и публичные превью по названию или описанию.';

  return (
    <>
      <PageHero>
        <PageIntro
          eyebrow="Датасеты"
          title={pageTitle}
          description={pageDescription}
          actions={<DatasetCatalogSwitch activeView={activeView} />}
        />
      </PageHero>
      <PageSection className="gap-6">
        <div className="space-y-6">
          <SurfacePanel className="gap-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-[var(--site-ink-strong)]">{searchTitle}</h2>
                <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">{searchDescription}</p>
              </div>
              <div className="w-full md:w-80">
                <div className="relative">
                  <Search className="absolute left-4 top-3.5 h-4 w-4 text-[var(--site-ink-subtle)]" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={searchTitle}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </SurfacePanel>

          {initialError ? (
            <EmptyState title="Каталог датасетов недоступен" description={initialError} />
          ) : activeView === 'public' ? (
            <>
              <DatasetSection
                title="Открытые датасеты"
                datasets={publicUtilityDatasets}
                emptyTitle={hasSearch ? 'Открытые датасеты не найдены.' : 'Открытые датасеты пока не опубликованы.'}
                actionLabel="Открыть датасет"
                showPreview
                timestampLabel="Обновлено"
                getTimestampValue={(dataset) => dataset.updatedAt}
              />

              {publicPreviewDatasets.length > 0 ? (
                <DatasetSection
                  title="Предпросмотр данных"
                  datasets={publicPreviewDatasets}
                  emptyTitle="Публичные превью не найдены."
                  actionLabel="Открыть превью"
                  showPreview
                  timestampLabel="Обновлено"
                  getTimestampValue={(dataset) => dataset.updatedAt}
                />
              ) : null}
            </>
          ) : (
            <DatasetSection
              title="Ваши датасеты"
              datasets={userDatasets}
              emptyTitle={hasSearch ? 'Датасеты не найдены.' : 'Датасетов пока нет.'}
              emptyDescription={hasSearch ? 'Попробуйте другой поисковый запрос.' : 'Запустите шаблон, чтобы создать первый.'}
              emptyAction={
                hasSearch ? undefined : (
                  <PublicLink href="/templates">
                    <Button>Открыть шаблоны</Button>
                  </PublicLink>
                )
              }
              actionLabel="Открыть датасет"
              showPreview={false}
              timestampLabel="Создано"
              getTimestampValue={(dataset) => dataset.createdAt}
              primaryAction
            />
          )}
        </div>
      </PageSection>
    </>
  );
}
